import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "../../global"
import API, { CalDAVAccountResponse, OccurrenceResponse } from "../../helpers/API"
import {
  FILTER_OCCURRENCES_SET,
  getOccurrences,
  SCHEDULE_SET_CURRENT_DAY,
} from "../../redux/actions"
import Calendar from "react-calendar"
import "./Calendar.scss"
import Linkify from "react-linkify"
import { calendarNumberOutline } from "ionicons/icons"

const MAX_TILE_MARKERS = 4

const Schedule: React.FC = () => {
  const isLoading = false

  const dispatch = useDispatch()
  const selectedDate = useSelector((state: AppState) => state.selectedDate)
  const occurrences = useSelector((state: AppState) => state.occurrences)
  const [calDAVAccount, setCalDAVAccount] = useState<CalDAVAccountResponse>()
  const [calDAVAlertOpen, setCalDAVAlertOpen] = useState(false)
  const [calDAVLoading, setCalDAVLoading] = useState(false)
  const [toastMessage, setToastMessage] = useState("")

  const selectedOccurrences = useSelector(
    (state: AppState) => state.selectedOccurrences
  )

  useEffect(() => {
    dispatch({
      type: SCHEDULE_SET_CURRENT_DAY,
      payload: new Date(),
    })
  }, [])

  useEffect(() => {
    getEventsFromDay(selectedDate)
  }, [selectedDate])

  useEffect(() => {
    if (selectedDate) {
      filterOccurrences(selectedDate)
    }
  }, [occurrences, selectedDate])

  const updateSelectedDate = (currDate: Date) => {
    dispatch({
      type: SCHEDULE_SET_CURRENT_DAY,
      payload: currDate,
    })
  }

  const formatCalendarDate = (date: Date) => {
    // El backend Radicale vol date stabili: zero-padding e timezone locale, senza sorprese UTC.
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, "0")
    const day = `${date.getDate()}`.padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const getDayRange = (date: Date) => {
    // Limiti locali del giorno: Radicale arriva con timezone, ma la UI ragiona sul dì selezionà.
    const dayStart = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0
    )
    const dayEnd = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    )
    return { dayStart, dayEnd }
  }

  const occursOnDay = (occurrence: OccurrenceResponse, date: Date) => {
    const { dayStart, dayEnd } = getDayRange(date)
    return occurrence.start <= dayEnd && occurrence.end > dayStart
  }

  const getOccurrencesForDay = (date: Date) => {
    return (occurrences || [])
      .filter((occurrence) => occursOnDay(occurrence, date))
      .sort((first, second) => first.start.getTime() - second.start.getTime())
  }

  const getCalendarMarkersForDay = (date: Date) => {
    const markerColors = getOccurrencesForDay(date).map(
      (occurrence) => occurrence.color || "var(--ion-color-medium)"
    )
    return markerColors.filter(
      (color, index) => markerColors.indexOf(color) === index
    )
  }

  const getEventsFromDay = (currDate: Date | undefined) => {
    if (currDate) {
      let today = currDate
      const first = new Date(today.getFullYear(), today.getMonth(), 1)
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)

      dispatch(
        getOccurrences(
          formatCalendarDate(first),
          formatCalendarDate(nextMonth),
          ""
        )
      )
    }
  }

  const formatEventTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const isAllDayEvent = (start: Date, end: Date, selectedDay: Date) => {
    const { dayStart, dayEnd } = getDayRange(selectedDay)
    const startsBeforeOrAtDay = start <= dayStart
    const endsAfterOrAtDay = end >= dayEnd
    return startsBeforeOrAtDay && endsAfterOrAtDay
  }

  const getDescriptionForEvent = (
    start: Date,
    end: Date,
    selectedDay: Date
  ) => {
    if (!selectedDay || isAllDayEvent(start, end, selectedDay)) {
      return ["Tutto il giorno", ""]
    }

    const { dayStart, dayEnd } = getDayRange(selectedDay)
    let strBefore = start < dayStart ? "Da prima" : formatEventTime(start)
    let strAfter = end > dayEnd ? "Continua" : formatEventTime(end)

    return [strBefore, strAfter]
  }

  const formatSelectedDayTitle = () => {
    if (!selectedDate) {
      return "Eventi"
    }

    return selectedDate.toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
    })
  }

  const renderTileMarkers = (date: Date) => {
    const markerColors = getCalendarMarkersForDay(date)
    if (!markerColors.length) {
      return null
    }

    return (
      <div className="schedule-tile-markers">
        {markerColors.slice(0, MAX_TILE_MARKERS).map((color, index) => (
          <span
            className="schedule-tile-marker"
            key={`${color}-${index}`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    )
  }

  const filterOccurrences = (selectedDate: Date) => {
    const selectedOccurrences = getOccurrencesForDay(selectedDate)

    dispatch({
      type: FILTER_OCCURRENCES_SET,
      payload: { selectedOccurrences },
    })
  }

  const openCalDAVInfo = async () => {
    setCalDAVLoading(true)
    try {
      const account = await API.getCalDAVAccount()
      setCalDAVAccount(account)
      setCalDAVAlertOpen(true)
    } catch (error) {
      // Se el backend no risponde, ghe demo un messaggio umano invece de una pagina muta.
      setToastMessage("Non riesco a caricare i dati CalDAV.")
    } finally {
      setCalDAVLoading(false)
    }
  }

  const copyCalDAVServer = async () => {
    if (!calDAVAccount?.server_url) {
      return
    }
    try {
      await navigator.clipboard.writeText(calDAVAccount.server_url)
      setToastMessage("Server CalDAV copiato.")
    } catch (error) {
      setToastMessage(calDAVAccount.server_url)
    }
  }

  const copyCalDAVCalendarUrl = async () => {
    if (!calDAVAccount?.default_calendar_url) {
      return
    }
    try {
      await navigator.clipboard.writeText(calDAVAccount.default_calendar_url)
      setToastMessage("URL CalDAV copiato.")
    } catch (error) {
      setToastMessage(calDAVAccount.default_calendar_url)
    }
  }

  return (
    <IonPage>
      <IonLoading cssClass="my-custom-class" isOpen={isLoading || calDAVLoading} message={calDAVLoading ? "Carico CalDAV..." : ""} />
      <IonAlert
        isOpen={calDAVAlertOpen}
        header="Calendario telefono"
        message={
          calDAVAccount
            ? `Per modificare gli eventi, aggiungilo come account CalDAV, non come calendario in abbonamento.<br/><br/>Server: ${calDAVAccount.server_url}<br/><br/>Se iOS lo cambia in https://app.ipaddi.net/ va bene: usa la discovery CalDAV automatica.<br/><br/>Utente: ${calDAVAccount.username}<br/>Password: password iPaddi<br/><br/>iPhone: Impostazioni > Calendario > Account > Aggiungi account > Altro > Aggiungi account CalDAV.<br/>`
            : ""
        }
        buttons={[
          { text: "Copia server", handler: copyCalDAVServer },
          { text: "Copia URL", handler: copyCalDAVCalendarUrl },
          { text: "Chiudi", role: "cancel" },
        ]}
        onDidDismiss={() => setCalDAVAlertOpen(false)}
      />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonTitle>Calendario</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={openCalDAVInfo}>
              <IonIcon icon={calendarNumberOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="schedule-page">
          <Calendar
            tileContent={({ date, view }) =>
              view === "month" ? renderTileMarkers(date) : null
            }
            onChange={(selectedDate: any) => {
              if (selectedDate instanceof Date) {
                updateSelectedDate(selectedDate)
              }
            }}
            onActiveStartDateChange={({ activeStartDate }) => {
              if (activeStartDate) {
                updateSelectedDate(activeStartDate)
              }
            }}
            value={selectedDate}
            defaultValue={new Date()}
          ></Calendar>

          <section className="schedule-agenda">
            <div className="schedule-agenda-header">
              <h2>{formatSelectedDayTitle()}</h2>
              <span>
                {selectedOccurrences?.length || 0}{" "}
                {(selectedOccurrences?.length || 0) === 1 ? "evento" : "eventi"}
              </span>
            </div>

            <IonList className="schedule-event-list is-list-colored">
              {selectedOccurrences?.map((occurrence: OccurrenceResponse) => {
                const eventTime = getDescriptionForEvent(
                  occurrence.start,
                  occurrence.end,
                  selectedDate!
                )

                return (
                  <React.Fragment key={occurrence.id}>
                    <IonItem
                      className="schedule-event-item is-item-category-secondary"
                      style={{ borderLeftColor: `${occurrence.color}` }}
                    >
                      <IonLabel className="ion-text-wrap">
                        <h2>{occurrence.title}</h2>
                        <p className="schedule-event-calendar">
                          {occurrence.calendar}
                        </p>

                        {occurrence.description && (
                          <p className="schedule-event-description">
                            <Linkify>{occurrence.description}</Linkify>
                          </p>
                        )}
                      </IonLabel>

                      <div slot="end" className="schedule-event-time">
                        <span>{eventTime[0]}</span>
                        {eventTime[1] && <span>{eventTime[1]}</span>}
                      </div>
                    </IonItem>
                  </React.Fragment>
                )
              })}
              {!selectedOccurrences?.length && (
                <div className="schedule-empty-day">
                  Nessun evento per questo giorno.
                </div>
              )}
            </IonList>
          </section>
        </div>
      </IonContent>
      <IonToast
        isOpen={!!toastMessage}
        message={toastMessage}
        duration={2500}
        onDidDismiss={() => setToastMessage("")}
      />
    </IonPage>
  )
}
export default Schedule
